import styled, { css } from 'styled-components'
import { Avatar } from '@components/Avatar'
import {
	backgroundOrange,
	vanilla,
	darkOrange,
	orange,
	gold,
	danger,
	dangerTint,
	blackOrange,
	black,
	textMuted,
	mutedGrey,
	surface2,
	warmTint,
} from '@/theme/colors'
import { radii, media, motion } from '@/theme/tokens'
import { fontSizes, text, tracking, leading } from '@/theme/typography'
import { dashedSlot } from '@/theme/recipes'
import { Button } from '@components/Button'
import { cardSurface } from '@components/Card'
import { Pill } from '@components/Pill'


export const LoadingContainer = styled.div`
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
    background-color: ${backgroundOrange};
    height: 100%;
`



export const DetailCard = styled.div`
	${cardSurface}
	padding: 0;
	overflow: hidden;
`

export const DetailHeader = styled.div`
    padding: 24px 24px 0;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;

    ${media.phone} {
        padding: 18px 16px 0;
        gap: 12px;
    }
`

export const DetailTitle = styled.h1`
    margin: 0;
    font-size: ${fontSizes.headingLg};
    font-weight: 700;
    color: ${black};
    line-height: ${leading.snug};
    word-break: break-word;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    ${media.phone} {
        font-size: ${fontSizes.title};
    }
`

export const ManualPill = styled(Pill).attrs({ $tone: 'dark' as const })``

export const ActionButtons = styled.div`
    display: flex;
    gap: 8px;
    flex-shrink: 0;
`

export const IconButton = styled(Button).attrs({ variant: 'secondary' as const, size: 'sm' as const })<{ $variant?: 'default' | 'danger' }>`
	width: 36px;
	height: 36px;
	padding: 0;

	${({ $variant }) =>
		$variant === 'danger' &&
		css`
			color: ${danger};
			border-color: ${danger}40;

			&:hover:not(:disabled) {
				background: ${dangerTint};
				color: ${danger};
				border-color: ${danger};
			}
		`}
`

export const DetailBody = styled.div`
    padding: 20px 24px 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;

    ${media.phone} {
        padding: 16px 16px 20px;
        gap: 18px;
    }
`

export const SectionLabel = styled.h3`
    ${text.eyebrow}
    color: ${darkOrange};
    margin: 0 0 12px;
`

export const LegsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

export const LayoverBanner = styled.div`
    text-align: center;
    font-size: ${fontSizes.micro};
    font-weight: 700;
    color: ${gold};
    padding: 6px 0;
`

/** $interactive=false renders the read-only (public) leg: no pointer, no hover. */
export const LegCard = styled.div<{ $isManual?: boolean; $interactive?: boolean }>`
    padding: 16px 18px;
    background: ${vanilla};
    border-radius: ${radii.inner};
    border: 1px solid ${({ $isManual }) => ($isManual ? `${blackOrange}22` : 'transparent')};
    display: flex;
    flex-direction: column;
    gap: 8px;
    cursor: ${({ $interactive = true }) => ($interactive ? 'pointer' : 'default')};
    transition: all ${motion.fast};

    &:hover {
        border-color: ${({ $interactive = true, $isManual }) =>
            $interactive ? `${orange}40` : $isManual ? `${blackOrange}22` : 'transparent'};
        background: ${({ $interactive = true }) => ($interactive ? `${gold}1f` : vanilla)};
    }
`

export const LegCode = styled.span`
    font-size: ${fontSizes.body};
    font-weight: 700;
    color: ${black};
`

export const LegRouteBlock = styled.div`
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 12px;
    padding: 8px 0 4px;
`

export const LegRouteEndpoint = styled.div<{ $align: 'start' | 'end' }>`
    display: flex;
    flex-direction: column;
    align-items: ${({ $align }) => ($align === 'start' ? 'flex-start' : 'flex-end')};
    text-align: ${({ $align }) => ($align === 'start' ? 'left' : 'right')};
    gap: 4px;
    min-width: 0;
`

export const LegRouteAirport = styled.span`
    font-size: ${fontSizes.headingLg};
    font-weight: 900;
    color: ${black};
    letter-spacing: ${tracking.tight};
    line-height: ${leading.none};

    ${media.phone} {
        font-size: ${fontSizes.title};
    }
`

export const LegRouteArrow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${orange};
    position: relative;
    min-width: 64px;
    padding: 0 4px;

    &::before,
    &::after {
        content: '';
        flex: 1;
        height: 0;
        border-top: 1.5px dashed ${orange}66;
        min-width: 14px;
    }

    svg {
        margin: 0 8px;
        flex-shrink: 0;
    }

    ${media.phone} {
        min-width: 40px;

        &::before,
        &::after {
            min-width: 8px;
        }
    }
`

export const LegTimeBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`

export const LegTimeLabel = styled.span`
    ${text.eyebrow}
    color: ${gold};
`

export const LegTimeValue = styled.span`
    font-size: ${fontSizes.small};
    font-weight: 700;
    color: ${black};
`

export const LegMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    font-size: ${fontSizes.micro};
    color: ${textMuted};
`

export const LegPassengerRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 6px;
`

export const LegEditHint = styled.span`
    font-size: ${fontSizes.micro};
    color: ${darkOrange};
    font-weight: 700;
    margin-left: auto;
`

export const AvatarStack = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
`

/** One face in the passenger / guest stack: overlaps the one before it and sits under it. */
export const StackedAvatar = styled(Avatar)<{ $index: number }>`
    margin-left: ${({ $index }) => ($index > 0 ? '-6px' : '0')};
    z-index: ${({ $index }) => 10 - $index};
`


export const PassengerCountText = styled.span`
    font-size: ${fontSizes.micro};
    color: ${textMuted};
    font-weight: 500;
    white-space: nowrap;
`

export const YouPill = styled(Pill).attrs({ $tone: 'success' as const })``

export const LinksList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

export const LinkItem = styled.a`
    font-size: ${fontSizes.small};
    color: ${orange};
    text-decoration: none;
    padding: 10px 12px;
    background: ${surface2};
    border-radius: ${radii.small};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    transition: background ${motion.fast};

    &:hover {
        background: ${gold}1f;
        text-decoration: underline;
    }
`

export const AttachmentButton = styled.button`
    font-size: ${fontSizes.small};
    color: ${orange};
    font-weight: 500;
    padding: 10px 12px;
    background: ${surface2};
    border: none;
    border-radius: ${radii.small};
    cursor: pointer;
    font-family: inherit;
    text-align: left;
    width: 100%;
    transition: background ${motion.fast};

    &:hover {
        background: ${gold}1f;
    }
`

export const NotesContent = styled.div`
    font-size: ${fontSizes.small};
    color: ${black};
    line-height: ${leading.body};
    white-space: pre-wrap;
    word-break: break-word;
    padding: 12px;
    background: ${surface2};
    border-radius: ${radii.small};
`

export const AddLegCallout = styled.button`
	${dashedSlot}
	color: ${gold};
`

export const MetaSection = styled.div`
    font-size: ${fontSizes.micro};
    color: ${mutedGrey};
    border-top: 1px solid ${warmTint};
    padding-top: 16px;
`

export const NotFoundMessage = styled.div`
    padding: 32px;
    text-align: center;
    color: ${textMuted};
`

export const LegCountBadge = styled.span`
    font-size: ${fontSizes.small};
    font-weight: 500;
    color: ${textMuted};
`
